import React, { useEffect, useMemo, useState } from 'react';
import { X, ClipboardList, Calendar, Clock, User, Link as LinkIcon, AlertCircle, Pencil } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
import { useAppData } from '../../contexts/AppDataContext';
import { useTasks } from '../../contexts/TaskContext';
export interface TaskData {
  title: string;
  description: string;
  assignee: string;
  dueDate: string;
  dueTime: string;
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  linkedTo?: {
    id: string;
    type: 'contact' | 'subscription' | 'order';
    label: string;
  };
  status: 'Open';
}
export interface EditTaskData {
  id: string;
  title: string;
  description?: string;
  assignee: string;
  dueDate: string;
  dueTime?: string;
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  linkedTo?: {
    id: string;
    type: string;
    label?: string;
  } | null;
}
interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (task: TaskData) => void;
  onUpdate?: (taskId: string, task: TaskData) => void;
  editTask?: EditTaskData | null;
  initialLinkedTo?: {
    id: string;
    type: 'contact' | 'subscription' | 'order';
    label: string;
  };
}
export const TEAM_MEMBERS = ['Current User', 'Sarah Admin', 'Mike Johnson', 'Chris Taylor', 'Sarah Chen'];
function parseDateToISO(dateStr: string): string {
  // Try parsing dates like "Feb 12 2026" or "Feb 18 2026"
  const parsed = new Date(dateStr);
  if (!isNaN(parsed.getTime())) {
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  // If already ISO format, return as-is
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return dateStr.split('T')[0];
  }
  return new Date().toISOString().split('T')[0];
}
export function CreateTaskModal({
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  editTask,
  initialLinkedTo
}: CreateTaskModalProps) {
  const {
    subscriptions
  } = useAppData();
  const {
    tasks
  } = useTasks();
  const isEditMode = !!editTask;
  // Handovers for the task being edited, newest first
  const assignmentHistory = [...(tasks.find(task => task.id === editTask?.id)?.assignmentHistory ?? [])].reverse();
  // Keep the current owner selectable even if they aren't in the default list
  const assigneeOptions = editTask && !TEAM_MEMBERS.includes(editTask.assignee) ? [editTask.assignee, ...TEAM_MEMBERS] : TEAM_MEMBERS;
  const [formData, setFormData] = useState<TaskData>({
    title: '',
    description: '',
    assignee: 'Current User',
    dueDate: new Date().toISOString().split('T')[0],
    dueTime: '',
    priority: 'Medium',
    status: 'Open',
    linkedTo: initialLinkedTo
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [showLinkResults, setShowLinkResults] = useState(false);
  // Deduplicate customers by email and filter by search query
  const linkSearchResults = useMemo(() => {
    if (!linkSearchQuery.trim()) return [];
    const query = linkSearchQuery.toLowerCase();
    const seen = new Set<string>();
    const results: Array<{
      id: string;
      type: 'contact' | 'subscription';
      label: string;
      subLabel: string;
    }> = [];
    for (const sub of subscriptions) {
      if (!seen.has(sub.customer)) {
        if (sub.customerName.toLowerCase().includes(query) || sub.customer.toLowerCase().includes(query)) {
          seen.add(sub.customer);
          results.push({
            id: sub.id,
            type: 'contact',
            label: sub.customerName,
            subLabel: sub.customer
          });
        }
      }
      if (sub.id.toLowerCase().includes(query) || sub.subscriptionTitle.toLowerCase().includes(query)) {
        results.push({
          id: sub.id,
          type: 'subscription',
          label: sub.id,
          subLabel: `${sub.customerName} — ${sub.subscriptionTitle}`
        });
      }
    }
    return results.slice(0, 8);
  }, [linkSearchQuery, subscriptions]);
  // Reset form when modal opens — pre-fill if editing
  useEffect(() => {
    if (isOpen) {
      if (editTask) {
        setFormData({
          title: editTask.title,
          description: editTask.description || '',
          assignee: editTask.assignee,
          dueDate: parseDateToISO(editTask.dueDate),
          dueTime: editTask.dueTime || '',
          priority: editTask.priority,
          status: 'Open',
          linkedTo: editTask.linkedTo ? {
            id: editTask.linkedTo.id,
            type: editTask.linkedTo.type as 'contact' | 'subscription' | 'order',
            label: editTask.linkedTo.label || editTask.linkedTo.id
          } : undefined
        });
      } else {
        setFormData({
          title: '',
          description: '',
          assignee: 'Current User',
          dueDate: new Date().toISOString().split('T')[0],
          dueTime: '',
          priority: 'Medium',
          status: 'Open',
          linkedTo: initialLinkedTo
        });
      }
      setErrors({});
      setLinkSearchQuery('');
      setShowLinkResults(false);
    }
  }, [isOpen, initialLinkedTo, editTask]);
  if (!isOpen) return null;
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.assignee) newErrors.assignee = 'Assignee is required';
    if (!formData.dueDate) newErrors.dueDate = 'Due date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (validate()) {
      if (isEditMode && onUpdate && editTask) {
        onUpdate(editTask.id, formData);
      } else {
        onCreate(formData);
      }
      onClose();
    }
  };
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-[rgba(15,23,42,0.5)] z-[1040]" onClick={onClose} data-id="element-2172" data-mp="1vvbmwxb6x0he" />

      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center z-[1050] p-4" data-id="element-2173" data-mp="22z1pl9fh2h6y">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-[560px] animate-[ocean-scale-in_200ms_ease-in-out] flex flex-col max-h-[calc(100vh-40px)]" data-id="element-2174" data-mp="27x34rj9s1wd5">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8F0]" data-id="element-2175" data-mp="2hn68adeiyspq">
            <div className="flex items-center gap-3" data-id="element-2176" data-mp="2mk6661180p5r">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isEditMode ? 'bg-[#FEF3C7]' : 'bg-[#E0F2FE]'}`} data-id="element-2177" data-mp="2qmt0c8ld6m64">
                {isEditMode ? <Pencil className="w-5 h-5 text-[#F59E0B]" data-id="element-2178" data-mp="2vn78rrqvny4o" /> : <ClipboardList className="w-5 h-5 text-[#0EA5E9]" data-id="element-2179" data-mp="30kz0aknm1i8c" />}
              </div>
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-2180" data-mp="30imn0kssrw1m">
                {isEditMode ? 'Edit Task' : 'Create Task'}
              </h2>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-colors" data-id="element-2181" data-mp="167hmvqgx61r1">
              <X className="w-5 h-5" data-id="element-2182" data-mp="twgnbnt65zcd" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-6 overflow-y-auto" data-id="element-2183" data-mp="owu9pd9fih31">
            <div className="space-y-5" data-id="element-2184" data-mp="ovapjbknjlvz">
              {/* Title */}
              <div data-id="element-2185" data-mp="k03roke2dbez">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2186" data-mp="jwfyxngg0t1b">
                  Title <span className="text-[#EF4444]" data-id="element-2187" data-mp="1hk87teov5ioq">*</span>
                </label>
                <input type="text" value={formData.title} onChange={e => setFormData({
                ...formData,
                title: e.target.value
              })} placeholder="e.g. Follow up on failed payment" className={`w-full h-10 px-3 border rounded-md bg-white text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20 transition-all ${errors.title ? 'border-[#EF4444] focus:border-[#EF4444]' : 'border-[#CBD5E1] focus:border-[#0EA5E9]'}`} data-id="element-2188" data-mp="1cnr6tjwkf9u8" />
                {errors.title && <p className="mt-1 text-[12px] text-[#EF4444] flex items-center gap-1" data-id="element-2189" data-mp="h0ddmm1xjg07">
                    <AlertCircle className="w-3 h-3" data-id="element-2190" data-mp="lv0rlqzb4s0j" /> {errors.title}
                  </p>}
              </div>

              {/* Description */}
              <div data-id="element-2191" data-mp="bye938uged1o">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2192" data-mp="bzz6d685ka38">
                  Description
                </label>
                <textarea value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} placeholder="Add details or context..." rows={3} className="w-full px-3 py-2.5 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none transition-all resize-none" data-id="element-2193" data-mp="zqurxlm0m921" />
              </div>

              {/* Assignee */}
              <div data-id="element-2194" data-mp="1nfxftb78sy4z">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2195" data-mp="383zx6n3wodmq">
                  Assignee <span className="text-[#EF4444]" data-id="element-2196" data-mp="335yfa5i7ukcu">*</span>
                </label>
                <div className="relative" data-id="element-2197" data-mp="3hubct6wycb9g">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" data-id="element-2198" data-mp="3ht9sscxykefl" />
                  <select value={formData.assignee} onChange={e => setFormData({
                  ...formData,
                  assignee: e.target.value
                })} className="w-full h-10 pl-9 pr-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none" data-id="element-2199" data-mp="3cwjadiyol6r0">
                    {assigneeOptions.map(member => <option key={member} value={member} data-id="element-2200" data-mp="2u3xkrgedacev">
                        {member}
                      </option>)}
                  </select>
                </div>
              </div>

              {/* Due Date & Time */}
              <div className="grid grid-cols-2 gap-4" data-id="element-2201" data-mp="9glzt8wlxws5">
                <div data-id="element-2202" data-mp="3fwlfd0m2g4dl">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2203" data-mp="3fx3m4r6cu0zr">
                    Due Date <span className="text-[#EF4444]" data-id="element-2204" data-mp="3az2140pdbrln">*</span>
                  </label>
                  <div className="relative" data-id="element-2205" data-mp="366iob5yi90hw">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" data-id="element-2206" data-mp="365h4abzih3o1" />
                    <input type="date" value={formData.dueDate} onChange={e => setFormData({
                    ...formData,
                    dueDate: e.target.value
                  })} className={`w-full h-10 pl-9 pr-3 border rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20 transition-all ${errors.dueDate ? 'border-[#EF4444] focus:border-[#EF4444]' : 'border-[#CBD5E1] focus:border-[#0EA5E9]'}`} data-id="element-2207" data-mp="2w9nomjqhwu21" />
                  </div>
                </div>
                <div data-id="element-2208" data-mp="giri7kq8292n">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2209" data-mp="gi86wmv2uixl">
                    Due Time
                  </label>
                  <div className="relative" data-id="element-2210" data-mp="nzy16stnycmg">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" data-id="element-2211" data-mp="t0c0nmh78e8v" />
                    <input type="time" value={formData.dueTime} onChange={e => setFormData({
                    ...formData,
                    dueTime: e.target.value
                  })} className="w-full h-10 pl-9 pr-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none transition-all" data-id="element-2212" data-mp="xrkagjmfy1mk" />
                  </div>
                </div>
              </div>

              {/* Priority */}
              <div data-id="element-2213" data-mp="k0youn5lrdkz">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2214" data-mp="k1gta72f4y3z">
                  Priority <span className="text-[#EF4444]" data-id="element-2215" data-mp="1qfg4fgkmlffn">*</span>
                </label>
                <div className="flex bg-[#F1F5F9] p-1 rounded-lg" data-id="element-2216" data-mp="1vf2s6jy2ye2p">
                  {['Low', 'Medium', 'High', 'Urgent'].map(priority => <button key={priority} type="button" onClick={() => setFormData({
                  ...formData,
                  priority: priority as any
                })} className={`flex-1 py-1.5 text-[13px] font-medium rounded-md transition-all ${formData.priority === priority ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#475569]'}`} data-id="element-2217" data-mp="3rho38nb6ue0t">
                      {priority}
                    </button>)}
                </div>
              </div>

              {/* Linked To */}
              <div data-id="element-2218" data-mp="33ufufvejwmu1">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2219" data-mp="33uzebhnrhlap">
                  Linked To
                </label>
                {formData.linkedTo ? <div className="flex items-center gap-2 p-2 bg-[#F0F9FF] border border-[#BAE6FD] rounded-md text-[#0284C7]" data-id="element-2220" data-mp="3ctkwvct8h0bh">
                    <LinkIcon className="w-4 h-4" data-id="element-2221" data-mp="2p2p6mk4fed6c" />
                    <span className="text-[13px] font-medium flex-1" data-id="element-2222" data-mp="2l36lpoclu23i">
                      {formData.linkedTo.label}
                    </span>
                    <button onClick={() => setFormData({
                  ...formData,
                  linkedTo: undefined
                })} className="p-1 hover:bg-[#E0F2FE] rounded-full transition-colors" data-id="element-2223" data-mp="2ax5efe83xi62">
                      <X className="w-3 h-3" data-id="element-2224" data-mp="20yfsnad3bdkj" />
                    </button>
                  </div> : <div className="relative" data-id="element-2225" data-mp="260542r485t95">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" data-id="element-2226" data-mp="2tphbpmjwlt9c" />
                    <input type="text" value={linkSearchQuery} onChange={e => {
                  setLinkSearchQuery(e.target.value);
                  setShowLinkResults(true);
                }} onFocus={() => {
                  if (linkSearchQuery.trim()) setShowLinkResults(true);
                }} onBlur={() => {
                  setTimeout(() => setShowLinkResults(false), 200);
                }} placeholder="Search contacts, subscriptions, or orders..." className="w-full h-10 pl-9 pr-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none transition-all" data-id="element-2227" data-mp="2youa94k50orr" />

                    {showLinkResults && linkSearchResults.length > 0 && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-50 max-h-[240px] overflow-y-auto" data-id="element-2228" data-mp="1jssx7ksgh7d8">
                        {linkSearchResults.map((result, idx) => <button key={`${result.type}-${result.id}-${idx}`} type="button" onMouseDown={e => {
                    e.preventDefault();
                    setFormData({
                      ...formData,
                      linkedTo: {
                        id: result.id,
                        type: result.type,
                        label: result.type === 'contact' ? `${result.label} (${result.subLabel})` : `${result.label} — ${result.subLabel}`
                      }
                    });
                    setLinkSearchQuery('');
                    setShowLinkResults(false);
                  }} className="w-full px-3 py-2.5 text-left hover:bg-[#F8FAFC] transition-colors flex items-center gap-3 border-b border-[#F1F5F9] last:border-b-0" data-id="element-2229" data-mp="2cg6ytw73mr3o" data-mpi={idx}>
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${result.type === 'contact' ? 'bg-[#E0F2FE] text-[#0EA5E9]' : 'bg-[#EDE9FE] text-[#6F42C1]'}`} data-id="element-2230" data-mp="37paa5bydyty7">
                              {result.type === 'contact' ? <User className="w-3.5 h-3.5" data-id="element-2231" data-mp="3hi7u7mj10qyx" /> : <LinkIcon className="w-3.5 h-3.5" data-id="element-2232" data-mp="3lgxelz2hf8s2" />}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-2233" data-mp="3qga3sqgtsrxt">
                              <div className="text-[13px] font-medium text-[#0F172A] truncate" data-id="element-2234" data-mp="3qeqn4yr9cevi">
                                {result.label}
                              </div>
                              <div className="text-[12px] text-[#94A3B8] truncate" data-id="element-2235" data-mp="1w4d82lbcozwy">
                                {result.subLabel}
                              </div>
                            </div>
                            <span className="text-[10px] font-medium text-[#94A3B8] uppercase tracking-wider flex-shrink-0" data-id="element-2236" data-mp="1t17e8a108b9d">
                              {result.type === 'contact' ? 'Contact' : 'Subscription'}
                            </span>
                          </button>)}
                      </div>}

                    {showLinkResults && linkSearchQuery.trim().length > 0 && linkSearchResults.length === 0 && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-50 px-4 py-3" data-id="element-2237" data-mp="2bxywqh8q2mcd">
                          <p className="text-[13px] text-[#94A3B8] text-center" data-id="element-2238" data-mp="2lq4njnn8mxpz">
                            No results found for "{linkSearchQuery}"
                          </p>
                        </div>}
                  </div>}
              </div>

              {/* Assignment history */}
              {isEditMode && assignmentHistory.length > 0 && <div data-id="element-2239" data-mp="2qq94tfvyzwh9">
                  <h3 className="text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2240" data-mp="2qmlbagqiza2j">
                    Assignment history
                  </h3>
                  <ul className="space-y-1" data-id="element-2241" data-mp="3eccpc1nmds0r">
                    {assignmentHistory.map((entry, index) => <li key={`${entry.date}-${index}`} className="text-[13px] text-[#0F172A]" data-id="element-2242" data-mp="30ctidc6jomq1" data-mpi={index}>
                        {entry.from} → {entry.to}{' '}
                        <span className="text-[#94A3B8]" data-id="element-2243" data-mp="359jyu0m9hkbz">
                          · {new Date(`${entry.date}T00:00:00`).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                        </span>
                      </li>)}
                  </ul>
                </div>}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E2E8F0] mt-auto" data-id="element-2244" data-mp="27ncsomab7jxi">
            <Button variant="outline" size="md" onClick={onClose} data-id="element-2245" data-mp="2clo2ix0ltufq">
              Cancel
            </Button>
            <Button variant="primary" size="md" onClick={handleSubmit} data-id="element-2246" data-mp="2s3fnx5pjdn6z">
              {isEditMode ? 'Save Changes' : 'Create Task'}
            </Button>
          </div>
        </div>
      </div>
    </>;
}